<template>
  <div
    class="scroll view"
    infinite-wrapper
    >
    <div class="faces-list">
      <owal-header-banner
        title="Faces Training Tool"
        :subtitle="this.dateFilterDescription()"
        :optional-view="OwalTrainingToolFilter"
        :options="options"
        :show-secondary-button="false"
        />
      <owal-banner
        v-if="newResults && newResults > 0"
        id="new-results"
        class="owal-banner__narrow magnet pull"
        >
        <div class="owal-banner__text-centered">
          Have {{ newResults }} new result{{ newResults > 1 ? 's' : '' }}.
          <router-link
            to="/detected"
            @click="updateResults"
            >
            Refresh
          </router-link> to load.
        </div>
      </owal-banner>
      <owal-training-tool-detection-banner
        v-for="(face, index) of faces"
        :key="face.frontend_face_uuid"
        :index="index"
        :detection="face"
        :shapes="params.shapes"
        :clahe="params.clahe"
        @reviewed="updateFaceAtIndex"
        />
    </div>
    <infinite-loading
      ref="infiniteLoading"
      force-use-infinite-wrapper="true"
      @infinite="getMoreResults"
      >
      <owal-spinner-banner slot="spinner" />
    </infinite-loading>
    <div style="width: 200px; margin: auto;" class="faces-training-tool__footer">
      <owal-banner v-if="!searching && faces && !faces.length" class=".owal-banner__narrow magnet pull">
        <div class="owal-banner__text-centered"> No Results </div>
      </owal-banner>
      <owal-banner v-if="oldestTime" class="owal-banner__narrow magnet pull">
        Showing results up to {{ oldestTime }}
      </owal-banner>
      <owal-banner v-if="!searching && !loadingMore && $route.params.frontend_face_uuid" class="owal-banner__narrow">
        <div class="owal-banner__text-centered">
          <button class="ui button" @click="getMoreResults">
            Load More
          </button>
        </div>
      </owal-banner>
    </div>
  </div>
</template>

<script>

import moment from 'moment-timezone'
import InfiniteLoading from 'vue-infinite-loading'

import { facematchconfirm, faceconfirm, facesdetected2 } from '@/libraries/api/faces'
import { mapGetters, mapActions, mapMutations } from 'vuex'

import { isValidUUID } from '@/libraries/util/uuid'
const OwalBanner = () => import('@/libraries/owal-ui/banners/OwalBanner')
const OwalSpinnerBanner = () => import('@/libraries/owal-ui/spinners/OwalSpinnerBanner')
const OwalHeaderBanner = () => import('@/libraries/owal-ui/banners/OwalHeaderBanner')
const OwalTrainingToolDetectionBanner = () => import('./OwalTrainingToolDetectionBanner')
const OwalTrainingToolFilter = () => import('./OwalTrainingToolFilter')

export default {
  name: 'FacesDetected',
  components: {
    InfiniteLoading,
    OwalHeaderBanner,
    OwalSpinnerBanner,
    OwalTrainingToolDetectionBanner,
    OwalBanner,
  },
  props: {
    faceId: {
      type: String,
    },
  },
  data () {
    return {
      faces: [],
      searching: true,
      newResults: 0,
      batchNumber: 0,
      loadingMore: false,
      oldestTime: '',
      refreshToken: '',
      tokenFormat: 'ddd, DD MMM YYYY HH:mm:ss',
      OwalTrainingToolFilter,
      subtitle: '',
    }
  },
  watch: {
    '$route': function () {
      const state = this.$state
      this.updateResults(state)
    },
    domainUUID: function () {
      this.updateFilters({ modelKey: undefined })
    },
  },
  computed: {
    ...mapGetters({
      isAuthenticated: 'isAuthenticated',
      filters: 'trainingTool/filters',
      isEditing: 'trainingTool/isEditing',
      cameraForId: 'cameraForUUID',
      frontendFaces: 'frontendFaces',
      domainUUID: 'currentDomainUUID',
    }),
    options () {
      return {
        optionalButtonTitle: 'Filter',
        optionalButtonSubtitle: this.subtitle,
      }
    },
    isSinglePerson () {
      const isSinglePerson = !!this.faceId
      return isSinglePerson
    },
    route () {
      if (!this.isSinglePerson) {
        return '/faces/'
      } else {
        if (this.facesConfirmedFiltered && this.facesConfirmedFiltered.length) {
          return '/faces/' + this.faceId
        }
        return '/faces'
      }
    },
    params () {
      const frontendFaceUUID = isValidUUID(this.faceId) ? this.faceId : undefined
      const params = {
        frontendFaceUUID,
        refreshToken: this.refreshToken,
        minimumFaceHeight: this.filters.faceHeight,
        domainUUID: this.domainUUID,
        cameraUUID: this.filters.cameraUUID,
        faceUUIDs: this.filters.faceUUIDs,
        confidence: this.filters.confidence,
        confidencePredicate: this.filters.confidencePredicate,
        limit: 20,
        shapes: this.filters.shapesOnFaces,
        clahe: this.filters.clahe,
        modelKey: this.filters.modelKey,
      }
      return params
    },
  },
  created () {
    this.resetTokenToMatchFilter()
    this.updateSubtitle()
    this.observeFilterChanges()
    this.setFilters({
      date: new Date().valueOf(),
    })
  },
  mounted () {
    this.updateFilters({ modelKey: undefined })
  },
  methods: {
    ...mapActions({
      setFilters: 'trainingTool/updateFilters',
      updateFilters: 'trainingTool/updateFilters',
    }),
    ...mapMutations({
      setFilteringModel: 'trainingTool/setFilteringModel',
    }),
    /**
     * Ensures that the refresh token is set to the current filter.
     * If a filter has not been set, use the current date to seed.
     */
    resetTokenToMatchFilter () {
      const unixTimestamp = this.filters.date || new Date.valueOf()
      const newToken = moment(unixTimestamp).utc().format(this.tokenFormat)
      this.updateRefreshToken(`${newToken} GMT`)
    },
    /**
     * Toggles the filter component.
     *
     * @param identifier The identifier of the button used to toggle.
     */
    handleFilterChange (identifier) {
      this.resetTokenToMatchFilter()
      this.faces = []
      this.batchNumber = 0
      this.newResults = 0
      const infiniteLoading = this.$refs.infiniteLoading
      if (infiniteLoading) {
        this.$nextTick(() => {
          this.updateSubtitle()
          infiniteLoading.$emit('$InfiniteLoading:reset')
        })
      }
    },
    /**
     * @param {Object} payload An object with a few keys, including `result`, `index`, and several others, depending on the value of `result`.
     */
    updateFaceAtIndex (payload) {
      console.log(`Detection reviewed with result: ${payload.result}`)
      const result = payload.result

      if (!result) {
        console.error(`Payload didn't include a result.`)
        return
      }

      if (result === 'confirmed') {
        this.confirmMatch(payload)
      } else if (result === 'not-face') {
        this.notAFace(payload)
      } else if (result === 'undo') {
        this.confirmMatch(Object.assign({}, payload, { isUndo: true }))
      }
    },
    /**
     * Confirms the identity of a face for a detection.
     *
     * @param detectionId The detection's UUID
     * @param frontendFaceUUID  The matching person's frontend_face_uuid.
     * @param index The index of the detection in this.faces.
     */
    confirmMatch ({ detectionId, frontendFaceUUID, index, isUndo = false }) {
      const detectionThatChanged = this.faces[index]

      let markAsConfirmed = isUndo ? undefined : true // Undefined is "undo" serverside.

      facematchconfirm(detectionId, frontendFaceUUID, markAsConfirmed, this.domainUUID).then(res => res.data).then(data => {
        if (data.ok) {
          const matchObject = detectionThatChanged.matches.find(x => x.frontend_face_uuid === frontendFaceUUID)
          let frontendMatchObject

          if (!matchObject) {
            // console.log('Could not find match_obj. Searching in frontendFaces...');
            frontendMatchObject = this.frontendFaces.find(x => x.frontend_face_uuid === frontendFaceUUID)
          }

          if (!frontendMatchObject && !matchObject) {
            // console.log(`Could not find match for frontend_face_uuid=${frontend_face_uuid} in matches or frontendFaces. Refreshing...`);
            this.updateResults()
            return
          }

          const newFaces = this.faces.slice()

          const cache = newFaces[index].undoCache

          if (matchObject) {
            if (cache) {
              newFaces[index].matches = cache
              newFaces[index].undoCache = undefined
            } else {
              newFaces[index].undoCache = newFaces[index].matches
              newFaces[index].matches = [matchObject]
            }
            matchObject.is_exact = !isUndo
          }

          if (frontendMatchObject) {
            const setme = {
              is_exact: !isUndo,
              person_name: frontendMatchObject.person_name,
              image_uri: (frontendMatchObject.images.find(img => img.orientation === 'front') || {}).thumbnail_uri,
            }

            if (cache) {
              newFaces[index].matches = cache
              newFaces[index].undoCache = undefined
            } else {
              newFaces[index].undoCache = newFaces[index].matches
              newFaces[index].matches = [setme]
            }

            newFaces[index].matches = [setme]
          }
          this.faces = newFaces
        } else {
          console.log(data.err)
        }
      }).catch((err) => {
        console.log(err)
      })
    },
    /**
     * Marks a detection as not being a face.
     */
    notAFace ({ detectionId, index }) {
      faceconfirm(detectionId, false, this.domainUUID)
        .then(res => res.data)
        .then(data => {
          if (data.ok) {
          // Editing the array directly in data() is ok. See bullet 2: https://vuejs.org/2016/02/06/common-gotchas/
            this.faces.splice(index, 1)
          } else {
            console.log(data.err)
          }
        }).catch((err) => {
          console.log(err)
        })
    },
    updateResults ($state) {
      this.searching = true
      const params = this.params
      facesdetected2(params).then(res => res.data).then(data => {
        if (!data.ok) {
          this.searching = false
          if ($state) {
            $state.complete()
          }
          return console.log(data.err)
        }
        if (data.oldest_time) {
          this.oldestTime = moment.utc(data.oldest_time).fromNow()
        }

        this.faces = data.faces
        if ($state) {
          $state.loaded()
        }
        this.searching = false
        this.newResults = data.new_results || 0
        this.updateRefreshToken(data.refresh_token)
        console.log(this.refreshToken)
      }).catch((err) => {
        console.log(err)
        $state.complete()
        this.searching = false
      })
    },
    getMoreResults ($state) {
      // console.log(`Getting more results, offset=${this.faces.length}`)
      this.loadingMore = true
      const params = this.params
      // console.log('params', params)
      facesdetected2(params).then(res => res.data).then(data => {
        if (!data.ok) {
          this.loadingMore = false
          if ($state) {
            $state.complete()
          }
          return console.log(data.err)
        }
        this.faces = this.faces.concat(data.faces)
        if ($state) {
          $state.loaded()
        }
        this.newResults = data.new_results
        this.batchNumber = this.batchNumber + 1
        this.loadingMore = false
        this.updateRefreshToken(data.refresh_token)
        if (data.oldest_time) {
          this.oldestTime = moment.utc(data.oldest_time).fromNow()
        }
        // console.log(this.oldestTime)
      })
    },
    /**
     * Update the refresh token.
     */
    updateRefreshToken (token) {
      // console.debug(`Setting refresh token to ${token}.`)
      if (token === null) {
        // console.debug(`Actually, not, because it's null.`)
        /**
         * Two options:
         *
         * 1. Show most nothing.
         * 2. Try to show something else.
         *    a. Show most recent.
         *    b. Show error message.
         *    c. Show both most recent and an error.
         */
        return
      }
      this.refreshToken = token
    },
    /**
     * Observe changes to the filters.
     */
    observeFilterChanges () {
      this.$store.subscribe((mutation, state) => {
        const filterChangeType = 'trainingTool/updateFilters'
        const modelChangeType = 'trainingTool/setFilteringModel'
        const domainChangeType = 'makeDomainCurrentByUUID'
        const isFilterChange = mutation.type === filterChangeType
        const isDomainChange = mutation.type === domainChangeType
        const isModelChange = mutation.type === modelChangeType
        if (isFilterChange || isDomainChange || isModelChange) {
          this.handleFilterChange()
        }
      })
    },
    dateFilterDescription () {
      return `${moment.utc(this.filters.date).tz('America/New_York').format('ddd, MM/DD/YYYY, h:mm:ss a')}`
    },
    cameraFilterDescription () {
      if (!this.filters.cameraUUID) {
        return ''
      }
      return `${this.cameraForId(this.filters.cameraUUID).description}`
    },
    sizeFilterDescription () {
      const heightFilter = this.filters.faceHeight
      const isNotNumber = isNaN(heightFilter)

      if (isNotNumber) {
        return ''
      }

      if (heightFilter === 0) {
        return ''
      }

      return `${heightFilter} px`
    },
    confidenceDescription () {
      if (!!this.params.confidence && !!this.params.confidencePredicate) {
        return `${this.params.confidencePredicate} ${this.params.confidence}`
      }
      return ''
    },
    facesFilterDescription () {
      if (!this.filters.faceUUIDs || this.filters.faceUUIDs.length === 0) {
        return ''
      }

      if (this.filters.faceUUIDs.length > 1) {
        return `${this.filters.faceUUIDs.length} Face`
      }
      return this.$store.getters.faceForUUID(this.filters.faceUUIDs[0]).person_name
    },
    shapesDescription () {
      let shapes = this.filters.shapesOnFaces ? 'Shapes' : ''
      if (this.filters.shapesOnFaces && this.filters.clahe) {
        shapes = 'Shapes (CLAHE)'
      }
      return shapes
    },
    updateSubtitle () {
      const threshold = 30
      let camera = this.cameraFilterDescription()
      if (camera.length > threshold) {
        camera = `${camera.substr(0, threshold)}...`
      }
      const parts = [
        camera,
        this.sizeFilterDescription(),
        this.facesFilterDescription(),
        this.confidenceDescription(),
        this.shapesDescription(),
      ].filter(part => part.length > 0)

      this.subtitle = parts.join(', ')
    },
  },
}
</script>

<style scoped>

.scroll.view {
  height: 100%;
  width: 100%;
  overflow: auto;
}

@media only screen and (min-width: 768px) {
  .scroll.view {
    padding: 4rem;
    margin-bottom: 4rem;
  }
}

#header {
  display: flex;
  flex-direction: column;
  align-content: center;
}

#header #back {
  align-self: flex-start;
}

#header #title {
  margin: 0;
  font-weight: bold;
}

.faces-list {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin: 0;
  padding: 0;
  width: 68.5714285714rem;
  margin: auto;
}

.owal-banner__narrow {
  display: flex;
  width: 200px;
  flex-grow: 0;
  flex-shrink: 0;
}
.owal-banner__text-centered {
  display: flex;
  justify-content: center;
  align-items: center;
  align-content: center;
  font-family: 'Lato-Bold';
  padding: 0.5rem;
  margin: 0.5714285715rem auto;
  text-align: center;
}

.faces-training-tool__footer {

}

</style>
